import type { StatusEffectInstance, StatusEffectId } from './effects'
import type { StatusCategory } from '../combat/status/meta'
import type {
  ArmorProfile,
  DamageType,
  EnemyRank,
  MovementClass
} from '../combat/types/primitives'
import {
  ENEMY_DEFINITIONS_V2,
  materializeEnemyDefinition
} from '../combat/enemy/catalog'
import type { RouteKey } from './path'

export interface EnemyRegenCapability {
  /** HP restored per simulated second after the delay. */
  hpPerSecond: number
  /** Simulated seconds after any damage before regen begins. */
  delayAfterDamage: number
}

export interface EnemyShieldCapability {
  maxShield: number
}

/**
 * Runtime enemy definition — stats at ACTIVE spawn defaults (Lv1 + default rank).
 * V2 metadata fields support targeting/damage/status resolvers without forking catalogs.
 */
export interface EnemyDefinition {
  id: string
  name: string
  maxHealth: number
  /** Cells per second along the logical path (unmodified base). */
  speed: number
  /** Gold granted once on kill. */
  reward: number
  leakDamage: number
  /** Logical radius in cell units. Renderer scales by render cell size. */
  radius: number
  color: number
  regen?: EnemyRegenCapability
  shield?: EnemyShieldCapability
  /** V2: default GROUND when omitted. */
  movementClass?: MovementClass
  defaultEnemyRank?: EnemyRank
  armorProfile?: ArmorProfile
  damageModifiers?: Partial<Record<DamageType, number>>
  immuneStatuses?: StatusEffectId[]
  immuneStatusCategories?: StatusCategory[]
  statusDurationMultiplier?: number
  statusCategoryDurationMultipliers?: Partial<Record<StatusCategory, number>>
  visualId?: string
}

/** Logical creep. Position and progress come from the session, not from a sprite. */
export interface EnemyInstance {
  id: string
  definitionId: string
  /** May be fractional so DoT can accumulate without rounding DPS away. */
  health: number
  maxHealth: number
  /** Current shield points (0 when definition has no shield). */
  shield?: number
  maxShield?: number
  /**
   * Simulated seconds remaining before regen may apply.
   * Reset to delayAfterDamage on any damage (including DoT).
   */
  regenDelayRemaining?: number
  /** Cleared at the start of each sim slice; set when damage lands in that slice. */
  regenDamagedThisSlice?: boolean
  /** Distance along the assigned route in cell units. */
  pathProgress: number
  /** Logical position in cell units. */
  position: { x: number; y: number }
  alive: boolean
  /** At most one active instance per status id. */
  statusEffects: StatusEffectInstance[]
  /** Spawn-resolved level (default 1). */
  enemyLevel?: number
  /** Spawn-resolved rank (default from definition). */
  enemyRank?: EnemyRank
  /**
   * Canonical route id assigned at spawn (ground-a / ground-b / air-a …).
   * Absent on hand-built instances, which fall back to routeKey.
   */
  routeId?: string
  /**
   * Total length of the assigned route, frozen at spawn.
   * Lets targeting compare progress across routes of different lengths.
   */
  routeLength?: number
  /**
   * Which map route this instance follows.
   * AIR → air route when present; otherwise ground (compat fallback).
   */
  routeKey?: RouteKey
  /** Kill gold when rank/level scaled away from catalog base. */
  killReward?: number
}

/** Materialized from combat V2 catalog — Lv1 identity with M41. */
export const NORMAL_ENEMY: EnemyDefinition = materializeEnemyDefinition(
  ENEMY_DEFINITIONS_V2.normal!
)
export const FAST_ENEMY: EnemyDefinition = materializeEnemyDefinition(ENEMY_DEFINITIONS_V2.fast!)
export const TANK_ENEMY: EnemyDefinition = materializeEnemyDefinition(ENEMY_DEFINITIONS_V2.tank!)
export const REGEN_ENEMY: EnemyDefinition = materializeEnemyDefinition(ENEMY_DEFINITIONS_V2.regen!)
export const SHIELD_ENEMY: EnemyDefinition = materializeEnemyDefinition(
  ENEMY_DEFINITIONS_V2.shield!
)
export const BOSS_ENEMY: EnemyDefinition = materializeEnemyDefinition(ENEMY_DEFINITIONS_V2.boss!)
export const FLYER_ENEMY: EnemyDefinition = materializeEnemyDefinition(ENEMY_DEFINITIONS_V2.flyer!)
export const AIR_BOSS_ENEMY: EnemyDefinition = materializeEnemyDefinition(
  ENEMY_DEFINITIONS_V2.air_boss!
)
export const GUARDIAN_ENEMY: EnemyDefinition = materializeEnemyDefinition(
  ENEMY_DEFINITIONS_V2.guardian!
)
export const PHASE_ENEMY: EnemyDefinition = materializeEnemyDefinition(ENEMY_DEFINITIONS_V2.phase!)
export const CRYSTAL_ENEMY: EnemyDefinition = materializeEnemyDefinition(
  ENEMY_DEFINITIONS_V2.crystal!
)

export const ENEMY_CATALOG: Record<string, EnemyDefinition> = {
  normal: NORMAL_ENEMY,
  fast: FAST_ENEMY,
  tank: TANK_ENEMY,
  regen: REGEN_ENEMY,
  shield: SHIELD_ENEMY,
  boss: BOSS_ENEMY,
  flyer: FLYER_ENEMY,
  air_boss: AIR_BOSS_ENEMY,
  guardian: GUARDIAN_ENEMY,
  phase: PHASE_ENEMY,
  crystal: CRYSTAL_ENEMY
}

/** Stable display order for UI / editor / summaries. */
export const ENEMY_TRAY_ORDER: EnemyDefinition[] = [
  NORMAL_ENEMY,
  FAST_ENEMY,
  TANK_ENEMY,
  REGEN_ENEMY,
  SHIELD_ENEMY,
  GUARDIAN_ENEMY,
  PHASE_ENEMY,
  CRYSTAL_ENEMY,
  BOSS_ENEMY,
  FLYER_ENEMY,
  AIR_BOSS_ENEMY
]

export const ENEMY_IDS = Object.keys(ENEMY_CATALOG)

export function createEnemyInstance(
  definition: EnemyDefinition,
  id: string,
  position: { x: number; y: number },
  options?: {
    enemyLevel?: number
    enemyRank?: EnemyRank
    routeKey?: RouteKey
    routeId?: string
    routeLength?: number
    maxHealth?: number
    killReward?: number
  }
): EnemyInstance {
  const maxShield = definition.shield?.maxShield ?? 0
  const movement = enemyMovementClass(definition)
  const routeKey =
    options?.routeId !== undefined
      ? routeKeyForRouteId(options.routeId)
      : options?.routeKey ?? (movement === 'AIR' ? 'air' : 'ground')
  const maxHealth = options?.maxHealth ?? definition.maxHealth
  return {
    id,
    definitionId: definition.id,
    health: maxHealth,
    maxHealth,
    shield: maxShield,
    maxShield,
    regenDelayRemaining: 0,
    regenDamagedThisSlice: false,
    pathProgress: 0,
    position: { ...position },
    alive: true,
    statusEffects: [],
    enemyLevel: options?.enemyLevel ?? 1,
    enemyRank: options?.enemyRank ?? definition.defaultEnemyRank ?? 'NORMAL',
    routeId: options?.routeId,
    routeLength: options?.routeLength,
    routeKey,
    killReward: options?.killReward
  }
}

/** Legacy route bucket for a canonical route id. */
export function routeKeyForRouteId(routeId: string): RouteKey {
  return routeId.startsWith('air') ? 'air' : 'ground'
}

export function enemyMovementClass(
  definition: Pick<EnemyDefinition, 'movementClass'> | undefined
): MovementClass {
  return definition?.movementClass ?? 'GROUND'
}

/** Route key for an instance — prefers routeId, then routeKey, else movement class. */
export function enemyRouteKey(
  enemy: Pick<EnemyInstance, 'routeKey' | 'definitionId'> & { routeId?: string },
  catalog: Record<string, Pick<EnemyDefinition, 'movementClass'> | undefined> = ENEMY_CATALOG
): RouteKey {
  if (enemy.routeId) return routeKeyForRouteId(enemy.routeId)
  if (enemy.routeKey === 'air' || enemy.routeKey === 'ground') return enemy.routeKey
  return enemyMovementClass(catalog[enemy.definitionId]) === 'AIR' ? 'air' : 'ground'
}
